/**
 * Badge component for status indicators.
 * 
 * @module components/ui/Badge
 */

import { cn } from '../../lib/utils';
import type { BadgeProps } from '../../types';

/**
 * Variant styles mapping.
 */
const variantStyles: Record<BadgeProps['variant'], string> = {
  success: 'bg-success-100 text-success-700 border-success-200',
  warning: 'bg-warning-100 text-warning-700 border-warning-200',
  error: 'bg-error-100 text-error-700 border-error-200',
  info: 'bg-secondary-100 text-secondary-700 border-secondary-200',
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
};

/**
 * Size styles mapping.
 */
const sizeStyles: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

/**
 * Badge component for displaying status labels.
 * 
 * @param props - Badge properties
 * @returns Badge element
 * 
 * @example
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error" size="sm">Failed</Badge>
 */
export function Badge({ variant, children, size = 'md', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {children}
    </span>
  );
}
